const DAY = 86400000;
export const dayNumber = value => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value || ""))) return null;
  const time = Date.parse(`${value}T00:00:00Z`);
  return Number.isFinite(time) && new Date(time).toISOString().slice(0, 10) === value ? time / DAY : null;
};
const isoDay = day => new Date(day * DAY).toISOString().slice(0, 10);
export function turnoverRange(referenceDate, days = 90) {
  const end = dayNumber(referenceDate);
  return end == null ? { from: "", to: "" } : { from: isoDay(end - days + 1), to: referenceDate };
}
export function overlapsTurnover(period, from, to) {
  const start = dayNumber(period.previousDate), end = dayNumber(period.currentDate);
  const first = dayNumber(from), last = dayNumber(to);
  return [start, end, first, last].every(value => value != null) && first <= last
    && end <= last && end > first && start < last + 1;
}
// Rates use units / observed days, never the mean of individual visit rates.
// Partial intersections are apportioned uniformly: dates of actual sales are unknown.
export function aggregateTurnover(history = [], { from, to, productId = "" } = {}) {
  const first = dayNumber(from), last = dayNumber(to);
  if (first == null || last == null || first > last) return [];
  const groups = new Map(), seen = new Set();
  const periods = [...history].filter(period => overlapsTurnover(period, from, to))
    .sort((a, b) => a.previousDate.localeCompare(b.previousDate) || a.currentDate.localeCompare(b.currentDate));
  for (const period of periods) {
    const key = JSON.stringify([period.pdvId, period.previousVisitId, period.currentVisitId]);
    if (seen.has(key)) continue;
    seen.add(key);
    const start = dayNumber(period.previousDate), end = dayNumber(period.currentDate);
    const days = end - start;
    if (days <= 0) continue;
    for (const line of period.lines || []) {
      if (productId && line.productId !== productId) continue;
      const groupKey = JSON.stringify([period.pdvId, line.productId]);
      const row = groups.get(groupKey) || { pdvId: period.pdvId, productId: line.productId, label: line.label,
        units: 0, observedDays: 0, validPeriods: 0, excludedPeriods: 0, partialPeriods: 0, lastEnd: -Infinity };
      if (line.status !== "estimated" || line.soldEstimated == null || !Number.isFinite(Number(line.soldEstimated)) || Number(line.soldEstimated) < 0) {
        row.excludedPeriods += 1;
      } else {
        const clippedStart = Math.max(start, first, row.lastEnd), clippedEnd = Math.min(end, last + 1);
        const coveredDays = Math.max(0, clippedEnd - clippedStart);
        if (coveredDays > 0) {
          row.units += Number(line.soldEstimated) * coveredDays / days;
          row.observedDays += coveredDays;
          row.validPeriods += 1;
          row.lastEnd = clippedEnd;
          if (period.isPartial) row.partialPeriods += 1;
        }
      }
      groups.set(groupKey, row);
    }
  }
  return [...groups.values()].map(({ lastEnd, ...row }) => {
    const perDay = row.observedDays > 0 ? row.units / row.observedDays : null;
    return { ...row, units: perDay == null ? null : row.units, perDay,
      perWeek: perDay == null ? null : perDay * 7, perMonth: perDay == null ? null : perDay * 30,
      perQuarter: perDay == null ? null : perDay * 90, windowDays: last - first + 1 };
  }).sort((a, b) => a.pdvId.localeCompare(b.pdvId) || a.label.localeCompare(b.label, "pt-BR"));
}
